import React, { useState } from 'react';
import { Alert, Button, Col, Row, Spinner } from 'react-bootstrap';
import { DragDropContext } from '@hello-pangea/dnd';
import { IconPackage, IconPlus, IconWand } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../shared/ConfirmContext';
import { formatWeight } from '../../../shared/units';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import EmptyState from '../../../components/feedback/EmptyState';
import PageSection from '../../../components/layout/PageSection';
import AddCaseForm from './cases/AddCaseForm';
import CaseCard from './cases/CaseCard';
import LooseItemsTable from './cases/LooseItemsTable';
import QuantityModal from './cases/QuantityModal';
import { packingErrorMessage, resolveDrop } from './cases/packingActions';
import usePacking from './cases/usePacking';

// Packing is only a plan: units and boxes stay in the warehouse until the contract is deployed.
const ProjectCases = ({ projectId }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const packing = usePacking(projectId);
  const [error, setError] = useState(null);
  const [showAddCase, setShowAddCase] = useState(false);
  const [autoAssignResult, setAutoAssignResult] = useState(null);
  const [quantityPrompt, setQuantityPrompt] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const data = packing.data || { bundles: [], loose_units: [], loose_quantities: [], summary: null };
  const bundles = data.bundles || [];

  // Runs a packing request; failures are shown as a message. Resolves to true on success.
  const run = async (request) => {
    setError(null);
    try {
      await request();
      return true;
    } catch (err) {
      setError(packingErrorMessage(t, err));
      return false;
    }
  };

  const askQuantity = (name, max) => new Promise((resolve) => setQuantityPrompt({ name, max, resolve }));
  const closePrompt = (value) => {
    quantityPrompt?.resolve(value);
    setQuantityPrompt(null);
  };

  const execute = async (action) => {
    let quantity = null;
    if (action.max !== null && action.max !== undefined) {
      quantity = action.max > 1 ? await askQuantity(action.name, action.max) : action.max;
      if (!quantity) return;
    }
    if (action.type === 'packUnit') {
      await run(() => packing.pack(action.bundleId, { item_unit: action.itemUnit }));
    } else if (action.type === 'packQuantity') {
      const body = { project_equipment: action.projectEquipment, quantity };
      if (action.sourceContent) body.source_content = action.sourceContent;
      await run(() => packing.pack(action.bundleId, body));
    } else if (action.type === 'unpack') {
      await run(() => packing.unpack(action.bundleId, action.content, quantity));
    }
  };

  const handleDragEnd = (result) => {
    const action = resolveDrop(result, data);
    if (action) execute(action);
  };

  const handleUnpack = (bundleId, content) => run(() => packing.unpack(bundleId, content.id));

  const handleRemove = async (bundle) => {
    const count = (bundle.contents || []).length;
    const message = count > 0 ? t('projects.cases.confirmRemoveWithItems', { count }) : t('projects.cases.confirmRemove');
    if (!await confirm(message)) return;
    await run(() => packing.removeCase(bundle.id));
  };

  const handleAutoAssign = async () => {
    setAutoAssignResult(null);
    await run(async () => setAutoAssignResult(await packing.autoAssign()));
  };

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (packing.isLoading) return <CenteredLoader />;

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <PageSection
        title={t('projects.cases.title')}
        description={t('projects.cases.description')}
        bodyClassName="p-0"
        actions={
          <>
            <Button variant="outline-secondary" size="sm" onClick={handleAutoAssign} disabled={packing.pending} title={t('projects.cases.autoAssignHint')}>
              {packing.pending ? <Spinner size="sm" className="me-1" /> : <IconWand size={16} className="me-1" />}
              {t('projects.cases.autoAssign')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowAddCase((v) => !v)}>
              <IconPlus size={16} className="me-1" />
              {t('projects.cases.addCase')}
            </Button>
          </>
        }
      >
        <div className="project-cases px-3 pt-3">
          {data.summary && bundles.length > 0 && (
            <div className="d-flex flex-wrap gap-4 mb-3 small" data-testid="packing-summary">
              <span><span className="text-muted">{t('projects.cases.summaryCases')}: </span><strong>{data.summary.cases}</strong></span>
              <span>
                <span className="text-muted">{t('projects.cases.summaryWeight')}: </span>
                <strong>{formatWeight(data.summary.total_weight_display, data.summary.weight_symbol)}</strong>
              </span>
            </div>
          )}

          {autoAssignResult && (
            <Alert variant="success" dismissible onClose={() => setAutoAssignResult(null)} className="app-inline-alert py-2 mb-3">
              {t('projects.cases.autoAssignResult', {
                createdCases: autoAssignResult.created_cases ?? 0,
                units: autoAssignResult.assigned_units,
                quantity: autoAssignResult.assigned_quantity,
                looseUnits: autoAssignResult.loose_units,
                looseQuantity: autoAssignResult.loose_quantity,
              })}
            </Alert>
          )}

          {error && <Alert variant="danger" className="app-inline-alert" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {packing.isError && <Alert variant="danger" className="app-inline-alert">{t('projects.cases.errorLoading')}</Alert>}

          {bundles.length > 0 && <small className="text-muted fst-italic d-block mb-3">{t('projects.cases.dragHint')}</small>}

          {showAddCase && (
            <AddCaseForm
              onAdd={(caseId, count, notes) => run(() => packing.addCases(caseId, count, notes))}
              onClose={() => setShowAddCase(false)}
            />
          )}

          {bundles.length === 0 ? (
            <EmptyState
              className="mb-3"
              icon={<IconPackage size={40} stroke={1.6} />}
              title={t('projects.cases.noCases')}
              message={t('projects.cases.noCasesHint')}
              action={
                <Button variant="primary" size="sm" onClick={() => setShowAddCase(true)}>
                  <IconPlus size={16} className="me-1" />
                  {t('projects.cases.addCase')}
                </Button>
              }
            />
          ) : (
            <Row xs={1} md={2} xl={3} className="g-3 mb-3">
              {bundles.map((bundle) => (
                <Col key={bundle.id}>
                  <CaseCard
                    bundle={bundle}
                    collapsed={!expanded.has(bundle.id)}
                    onToggle={() => toggle(bundle.id)}
                    onRemove={handleRemove}
                    onUnpack={(content) => handleUnpack(bundle.id, content)}
                    onCaseUnitChange={(b, caseUnit) => run(() => packing.setCaseUnit(b.id, caseUnit))}
                    busy={packing.pending}
                  />
                </Col>
              ))}
            </Row>
          )}

          <LooseItemsTable looseUnits={data.loose_units} looseQuantities={data.loose_quantities} />
        </div>
      </PageSection>
      <QuantityModal prompt={quantityPrompt} onConfirm={closePrompt} onCancel={() => closePrompt(null)} />
    </DragDropContext>
  );
};

export default ProjectCases;
